import { on } from 'events';

import React, { useState, useMemo, useEffect } from 'react';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { FaAngleDown, FaAngleUp, FaArrowRight } from 'react-icons/fa6';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import OffCanvas from '../../components/OffCanvas';

// Styled-component para a seção da tabela
const SectionTabela = styled.div<{ minHeight?: string }>`
  padding: 0px 5px 0px 5px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid #e5e5e5;
  border-radius: 20px;
  max-height: 100%;
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
  overflow-y: auto;
  position: relative;
`;

const FixedPaginationContainer = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 1px;
  background: ${({ theme }) => theme.colors.white};
  z-index: 1;
`;

const StyledTable = styled.table`
  vertical-align: middle;
  &:hover {
    cursor: pointer;
  }
  th,
  td {
    padding: 12px 15px;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${({ theme }) => theme.colors.white};
  }
  th {
    color: ${({ theme }) => theme.colors.tabela.fonteTh};
    border-bottom: 2px solid ${({ theme }) => theme.colors.tabela.linha};
    cursor: pointer;
  }
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.tabela.linha};
    color: ${({ theme }) => theme.colors.tabela.fonteTd};
  }
`;

const OffCanvasContent = styled.div`
  display: grid;
  grid-template-rows: auto;
  height: 100%;
  width: 100%;
  td {
    text-align: start;
  }
  div {
    display: flex;
    padding: 0px;
  }
`;

const OffCanvasContentData = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: white;
  border-radius: 10px;
  table {
    border: none;
    flex-grow: 1;
    max-height: 95%;
    overflow-y: auto;
    border-spacing: 0;
    border-collapse: collapse;
  }
  tbody {
    padding: 10px;
    display: block;
    max-height: 95%;
    overflow-y: auto;
  }
  tr {
    display: table;
    width: 100%;
  }
  td,
  th {
    text-align: start;
  }
  div {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0px;
    height: 5%;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
`;

const Th = styled.th`
  border-bottom: 0.25px solid #e9e8e8;
  padding: 8px;
  padding-left: 15px;
  width: 30%;
`;

const Td = styled.td`
  border-bottom: 0.25px solid #e9e8e8;
  padding: 8px;
  padding-left: 15px;
  width: 70%;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const SeparadorTh = styled.th`
  background-color: #e2e2e2a9;
  padding: 0px;
  height: 1px;
  line-height: 1px;
  width: 30%;
`;

const Separador = styled.td`
  background-color: #e2e2e2a9;
  padding: 0px;
  height: 10px;
  line-height: 1px;
  width: 70%;
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 20px;
  p {
    color: ${({ theme }) => theme.colors.tabela.fonteTh};
    margin-bottom: 10px;
    font: normal normal medium 0.875rem/1.1875rem Rawline;
  }
  span {
    color: ${({ theme }) => theme.colors.tabela.fonteTh};
  }
  .showing-text {
    color: ${({ theme }) => theme.colors.tabela.fonteTd};
    margin-bottom: 10px;
  }
  .total-text {
    color: ${({ theme }) => theme.colors.tabela.fonteTh};
  }
  .pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .pagination {
    display: flex;
    list-style: none;
    padding: 0;
  }
  .page-link {
    display: block;
    padding: 5px 10px;
    color: ${({ theme }) => theme.colors.tabela.fonteTh};
    text-decoration: none;
    border: 1px solid ${({ theme }) => theme.colors.tabela.paginaBd};
    height: 37px;
    width: 37px;
    text-align: center;
    cursor: pointer;
  }
  .page-link:focus {
    outline: none;
  }
  .page-item.active .page-link {
    background-color: ${({ theme }) => theme.colors.tabela.fundo};
    color: ${({ theme }) => theme.colors.tabela.fonteActive};
    letter-spacing: 0px;
    opacity: 1;
  }
  .page-item.disabled .page-link {
    pointer-events: none;
    background-color: ${({ theme }) => theme.colors.tabela.fundo};
    color: ${({ theme }) => theme.colors.tabela.fonteTh};
  }
  .page-item:first-child .page-link {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }
  .page-item:last-child .page-link {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
  .select-container {
    position: relative;
    display: inline-block;
    margin-left: 15px;
  }
  .angle-down-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.tabela.fonteTh};
    pointer-events: none;
  }
  select {
    height: 37px;
    padding: 8px 30px 8px 15px;
    border: 1px solid ${({ theme }) => theme.colors.tabela.linha};
    border-radius: 10px;
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.tabela.fonteTd};
    font: normal normal medium 0.875rem/1.1875rem Rawline;
    cursor: pointer;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="0" height="0"><path d="" fill="none"/></svg>');
    background-repeat: no-repeat;
    background-position: right 10px center;
  }
  select:focus {
    outline: none;
    border: 1px solid ${({ theme }) => theme.colors.tabela.linha};
  }
  .pagination-form {
    display: flex;
    align-items: center;
    margin-left: 10px;
    padding: 5px;
    padding-left: 15px;
    padding-right: 45px;
    height: 37px;
    label {
      display: flex;
      align-items: center;
      margin-right: 10px;
      position: relative;
    }
    .input-container {
      position: relative;
      display: flex;
      align-items: center;
    }
    .page-input {
      width: 80px;
      padding: 5px;
      padding-left: 20px;
      padding-right: 30px;
      border: 1px solid ${({ theme }) => theme.colors.tabela.linha};
      color: ${({ theme }) => theme.colors.tabela.fonteTd};
      border-radius: 10px;
      text-align: left;
      font: normal normal medium 0.875rem/1.1875rem Rawline;
      width: 189px;
    }
    .page-input:focus {
      outline: none;
    }
    .page-button {
      position: absolute;
      right: 15px;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      color: ${({ theme }) => theme.colors.tabela.fonteTh};
      font-size: 1rem;
      border-left: 1px solid ${({ theme }) => theme.colors.tabela.linha};
      height: 100%;
      padding-left: 10px;
    }
  }
`;

const CenteredMessage = styled.p`
  text-align: center;
  font-size: 1.1rem;
  margin-top: 2rem;
`;

interface TabelaProps {
  headers: {
    text: string;
    width: number;
    key: string;
  }[];
  dataTable: any[];
  dataOffCanvas?: any[];
  dataRaw?: any[];
  displayValues?: Record<string, string>;
  itemsPerPage?: number;
  minHeight?: string;
  rowClickable?: boolean;
  resultRow?: boolean;
  showPagination?: boolean;
  onRowClick?: any;
  noOffCanvasTable?: boolean;
  tituloCanvas?: string;
  pathOffcanvas?: string;
  botaoAbrirOffCanvas?: boolean;
  mostrarBotaoCopiar?: boolean;
  onAbrirOffCanvasClick?: (selectedRow: any) => void;
}

const itemsPerPageOptions = [5, 10, 20, 50, 100];

// helper: converte unknown -> ReactNode mantendo sua lógica atual
const asNode = (key: string, value: unknown): React.ReactNode => {
  if (typeof value === 'string' && key.startsWith('data')) return value;
  if (value == null) return '';
  if (React.isValidElement(value)) return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  return value as React.ReactNode;
};

const Tabela: React.FC<TabelaProps> = ({
  headers,
  dataTable,
  dataOffCanvas,
  displayValues,
  itemsPerPage: itemsPerPageProp = 50,
  minHeight,
  rowClickable = true,
  resultRow = false,
  showPagination = true,
  onRowClick,
  noOffCanvasTable,
  tituloCanvas = '',
  pathOffcanvas = '',
  botaoAbrirOffCanvas = true,
  mostrarBotaoCopiar = true,
  onAbrirOffCanvasClick,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showOffCanvas, setShowOffCanvas] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [ascending, setAscending] = useState<boolean>(true);
  const [itemsPerPageState, setItemsPerPageState] = useState(itemsPerPageProp);
  const location = useLocation();
  const navigate = useNavigate();

  // Verificação de IDs duplicados
  useEffect(() => {
    if (dataTable && dataTable.length > 0) {
      const ids = dataTable.map((item) => item.id);
      const hasDuplicates = new Set(ids).size !== ids.length;

      if (hasDuplicates) {
        console.error('Atenção: Existem IDs duplicados nos dados da tabela!');
        const duplicateIds = ids.filter(
          (id, index) => ids.indexOf(id) !== index,
        );
      }
    }
  }, [dataTable]);

  // Reinicia a página ao aplicar ordenação
  useEffect(() => {
    setCurrentPage(1);
  }, [sortColumn, ascending]);

  const indexOfLastItem = currentPage * itemsPerPageState;
  const indexOfFirstItem = indexOfLastItem - itemsPerPageState;

  const handleShowOffCanvas = (rowIndex: number) => {
    const originalIndex = indexOfFirstItem + rowIndex;
    const row = dataOffCanvas?.[originalIndex];

    if (row) {
      setSelectedRow(row);
      setShowOffCanvas(true);
    }
  };

  const handleHideOffCanvas = () => {
    setShowOffCanvas(false);
    setSelectedRow(null);
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (columnKey: string) => {
    if (columnKey === sortColumn) {
      setAscending(!ascending);
    } else {
      setSortColumn(columnKey);
      setAscending(true);
    }
  };

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newItemsPerPage = Number(e.target.value);
    setItemsPerPageState(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRowClick = (row: any) => {
    if (rowClickable) {
      if (onRowClick) {
        resultRow ? onRowClick(row) : onRowClick(row.id);
      } else {
        navigate(`${row.id}/dadosgerais`);
      }
    }
  };

  const sortedAndPaginatedData = useMemo(() => {
    if (sortColumn) {
      const sortedData = [...dataTable].sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return ascending ? aValue - bValue : bValue - aValue;
        } else if (typeof aValue === 'string' && typeof bValue === 'string') {
          return ascending
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        return 0;
      });

      return sortedData.slice(indexOfFirstItem, indexOfLastItem);
    }

    return dataTable.slice(indexOfFirstItem, indexOfLastItem);
  }, [dataTable, sortColumn, ascending, indexOfFirstItem, indexOfLastItem]);

  return (
    <SectionTabela minHeight={minHeight}>
      <StyledTable className="table fixed-size-table">
        <thead>
          <tr>
            {headers.map((header, idx) => {
              const columnKeyRaw = (header.key ?? '').trim();
              const sortable = columnKeyRaw.length > 0;

              // slug do texto como fallback (evita keys duplicadas "header-")
              const slug =
                (header.text ?? '')
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/(^-|-$)/g, '') || `col-${idx}`;

              const safeKey = columnKeyRaw || slug || `col-${idx}`;

              return (
                <th
                  key={`header-${safeKey}`}
                  style={{ width: header.width ? `${header.width}%` : 'auto' }}
                  onClick={
                    sortable ? () => handleSort(columnKeyRaw) : undefined
                  }
                  aria-sort={
                    sortColumn === columnKeyRaw
                      ? ascending
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  data-col-key={safeKey}
                >
                  {header.text}
                  {sortColumn === columnKeyRaw && (
                    <span>{ascending ? <FaAngleUp /> : <FaAngleDown />}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {sortedAndPaginatedData.map((row, rowIndex) => (
            <tr key={`row-${row.id}-${rowIndex}`}>
              {!noOffCanvasTable && (
                <td key={`zoom-${row.id}-${rowIndex}`}>
                  <span
                    onClick={() => handleShowOffCanvas(rowIndex)}
                    className="material-icons"
                  >
                    zoom_in
                  </span>
                </td>
              )}
              {Object.keys(row).map((key, cellIndex) => (
                <td
                  key={`cell-${row.id}-${key}-${cellIndex}`}
                  onClick={() => handleRowClick(row)}
                >
                  {row[key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledTable>

      {showOffCanvas && (
        <OffCanvas
          show={showOffCanvas}
          onHide={handleHideOffCanvas}
          placement="end"
          name={'Detalhes do registro'}
          style={{ width: '40%' }}
          currentUrl={location.pathname}
          selectedRowId={selectedRow.id}
          displayValues={displayValues}
          selectedRow={selectedRow}
          tituloCanvas={tituloCanvas}
          path={pathOffcanvas}
          botaoAbrir={botaoAbrirOffCanvas}
          mostrarBotaoCopiar={mostrarBotaoCopiar}
          onAbrirClick={onAbrirOffCanvasClick}
        >
          <OffCanvasContent>
            <OffCanvasContentData>
              <Table>
                {selectedRow !== null && selectedRow !== undefined && (
                  <tbody>
                    {Object.entries(selectedRow).map(
                      ([key, value], index, array) => {
                        const isNextRowSeparator =
                          array[index + 1]?.[0]?.includes('separador');

                        return (
                          <tr
                            key={`offcanvas-row-${selectedRow.id}-${key}-${index}`}
                          >
                            {key.includes('separador') ? (
                              <>
                                <SeparadorTh>
                                  {(displayValues && displayValues[key]) ?? key}
                                </SeparadorTh>
                                <Separador>{asNode(key, value)}</Separador>
                              </>
                            ) : (
                              <>
                                <Th
                                  style={{
                                    borderBottom: isNextRowSeparator
                                      ? 'none'
                                      : '0.25px solid #e8e9e9',
                                  }}
                                >
                                  {(displayValues && displayValues[key]) ?? key}
                                </Th>
                                <Td
                                  style={{
                                    borderBottom: isNextRowSeparator
                                      ? 'none'
                                      : '0.25px solid #e9e8e8',
                                  }}
                                >
                                  {asNode(key, value)}
                                </Td>
                              </>
                            )}
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                )}
              </Table>
            </OffCanvasContentData>
          </OffCanvasContent>
        </OffCanvas>
      )}

      {dataTable && dataTable.length > 0 ? (
        <FixedPaginationContainer>
          {showPagination && (
            <Pagination
              itemsPerPage={itemsPerPageState}
              totalItems={dataTable.length}
              paginate={paginate}
              currentPage={currentPage}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              handleItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={itemsPerPageOptions}
            />
          )}
        </FixedPaginationContainer>
      ) : (
        <CenteredMessage>Nenhum registro encontrado</CenteredMessage>
      )}
    </SectionTabela>
  );
};

interface PaginationProps {
  itemsPerPage: number;
  totalItems: number;
  paginate: (pageNumber: number) => void;
  currentPage: number;
  indexOfFirstItem: number;
  indexOfLastItem: number;
  handleItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  itemsPerPageOptions: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  itemsPerPage,
  totalItems,
  paginate,
  currentPage,
  indexOfFirstItem,
  indexOfLastItem,
  handleItemsPerPageChange,
  itemsPerPageOptions,
}) => {
  const [goToPage, setGoToPage] = useState('');

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(totalItems / itemsPerPage); i++) {
    pageNumbers.push(i);
  }

  const maxPagesToShow = 7;
  const halfPagesToShow = Math.floor(maxPagesToShow / 2);
  let startPage = currentPage - halfPagesToShow;
  let endPage = currentPage + halfPagesToShow;

  if (startPage <= 0) {
    startPage = 1;
    endPage = maxPagesToShow;
  }

  if (endPage > pageNumbers.length) {
    endPage = pageNumbers.length;
    startPage = endPage - maxPagesToShow + 1;
    if (startPage <= 0) {
      startPage = 1;
    }
  }

  const handleGoToPage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const pageNumber = Number(goToPage);
    if (pageNumber > 0 && pageNumber <= pageNumbers.length) {
      paginate(pageNumber);
    }
    setGoToPage('');
  };

  return (
    <PaginationContainer>
      <p className="showing-text">
        <span className="total-text">Mostrando: </span>
        {indexOfFirstItem + 1} a{' '}
        {indexOfLastItem > totalItems ? totalItems : indexOfLastItem} /{' '}
        <span className="total-text">Total de</span> {totalItems}
      </p>
      <nav>
        <div className="pagination-container">
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <a
                onClick={() => paginate(currentPage - 1)}
                className="page-link"
              >
                <FaAngleLeft />
              </a>
            </li>
            {pageNumbers.slice(startPage - 1, endPage).map((number) => (
              <li
                key={`page-${number}`}
                className={`page-item ${currentPage === number ? 'active' : ''}`}
              >
                <a onClick={() => paginate(number)} className="page-link">
                  {number}
                </a>
              </li>
            ))}
            <li
              className={`page-item ${currentPage === pageNumbers.length ? 'disabled' : ''}`}
            >
              <a
                onClick={() => paginate(currentPage + 1)}
                className="page-link"
              >
                <FaAngleRight />
              </a>
            </li>
          </ul>

          <ul className="pagination">
            <li>
              <div className="select-container">
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="items-per-page-select"
                >
                  {itemsPerPageOptions.map((option) => (
                    <option key={`option-${option}`} value={option}>
                      Mostrar {option}
                    </option>
                  ))}
                </select>
                <FaAngleDown className="angle-down-icon" />
              </div>
            </li>
            <li>
              <form onSubmit={handleGoToPage} className="pagination-form">
                <label>
                  <div className="input-container">
                    <input
                      type="text"
                      className="page-input"
                      placeholder="Página"
                      value={goToPage}
                      onChange={(e) => setGoToPage(e.target.value)}
                    />
                    <button type="submit" className="page-button">
                      <FaArrowRight />
                    </button>
                  </div>
                </label>
              </form>
            </li>
          </ul>
        </div>
      </nav>
    </PaginationContainer>
  );
};

export default Tabela;
